import { Participant, Match } from '../models';
import { MatchRepository } from '../repositories/MatchRepository';
import { ParticipantRepository } from '../repositories/ParticipantRepository';

export interface SwissStandingResult {
  participant: Participant;
  rank: number;
  points: number;
  matchesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  buchholz: number;
  opponentIds: string[];
}

export class GetSwissStandingsUseCase {
  constructor(
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<SwissStandingResult[]> {
    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    const matches = await this.matchRepository.getByTournamentId(tournamentId);

    // Initialize standings
    const standingsMap = new Map<
      string,
      {
        participant: Participant;
        points: number;
        wins: number;
        losses: number;
        draws: number;
        matchesPlayed: number;
        opponents: Set<string>;
      }
    >();

    for (const p of participants) {
      standingsMap.set(p.id, {
        participant: p,
        points: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        matchesPlayed: 0,
        opponents: new Set(),
      });
    }

    // Process completed matches
    for (const match of matches) {
      if (match.status !== 'completed') continue;

      const p1Id = match.participant1Id;
      const p2Id = match.participant2Id;

      if (!p1Id) continue;

      const p1 = standingsMap.get(p1Id);
      const p2 = p2Id ? standingsMap.get(p2Id) : undefined;

      if (!p1) continue;

      // Handle bye
      if (!p2Id) {
        p1.points += 1;
        p1.wins += 1;
        p1.matchesPlayed += 1;
        continue;
      }

      if (!p2) continue;

      // Track opponents
      p1.opponents.add(p2Id);
      p2.opponents.add(p1Id);

      p1.matchesPlayed += 1;
      p2.matchesPlayed += 1;

      // Award points based on winner
      if (match.winnerId === p1Id) {
        p1.points += 1;
        p1.wins += 1;
        p2.losses += 1;
      } else if (match.winnerId === p2Id) {
        p2.points += 1;
        p2.wins += 1;
        p1.losses += 1;
      } else {
        // Draw
        p1.points += 0.5;
        p2.points += 0.5;
        p1.draws += 1;
        p2.draws += 1;
      }
    }

    // Calculate Buchholz tiebreaker
    const standingsWithBuchholz = Array.from(standingsMap.values()).map(standing => ({
      ...standing,
      buchholz: Array.from(standing.opponents)
        .map(oppId => standingsMap.get(oppId)?.points || 0)
        .reduce((sum, points) => sum + points, 0),
    }));

    // Sort by points (desc), then buchholz (desc)
    standingsWithBuchholz.sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      return b.buchholz - a.buchholz;
    });

    // Assign ranks
    return standingsWithBuchholz.map((standing, index) => ({
      participant: standing.participant,
      rank: index + 1,
      points: standing.points,
      matchesPlayed: standing.matchesPlayed,
      wins: standing.wins,
      losses: standing.losses,
      draws: standing.draws,
      buchholz: standing.buchholz,
      opponentIds: Array.from(standing.opponents),
    }));
  }
}
